import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Divider
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Medication as MedicationIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { api } from '@services/authService';
import { useAuth } from '@contexts/AuthContext';

interface PrescriptionItem {
  medication_name: string;
  dosage: string;
  frequency: string;
  duration: string;
  instructions: string;
}

interface Prescription {
  id: string;
  patient_id: string;
  created_at: string;
  status: string;
  items: PrescriptionItem[];
  patient?: any;
}

const DoctorPrescriptions: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [prescriptionItems, setPrescriptionItems] = useState<PrescriptionItem[]>([
    { medication_name: '', dosage: '', frequency: '', duration: '', instructions: '' }
  ]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadPrescriptions();
  }, []);

  const loadPrescriptions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/prescriptions');
      setPrescriptions(response.data.data);
    } catch (error) {
      console.error('Eroare la incarcarea retetelor:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setPrescriptionItems([
      ...prescriptionItems,
      { medication_name: '', dosage: '', frequency: '', duration: '', instructions: '' }
    ]);
  };

  const handleRemoveItem = (index: number) => {
    setPrescriptionItems(prescriptionItems.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: keyof PrescriptionItem, value: string) => {
    const updated = [...prescriptionItems];
    updated[index][field] = value;
    setPrescriptionItems(updated);
  };

  const handleSavePrescription = async () => {
    if (!selectedPatientId) {
      alert('Selecteaza un pacient');
      return;
    }

    const validItems = prescriptionItems.filter(item => item.medication_name && item.dosage);
    
    if (validItems.length === 0) {
      alert('Adauga cel putin un medicament');
      return;
    }

    try {
      await api.post('/prescriptions', {
        patient_id: selectedPatientId,
        clinic_id: user?.clinicId,
        status: 'active',
        issue_immediately: true,
        items: validItems.map((it) => ({
          medication_name: it.medication_name,
          dosage: it.dosage,
          frequency: it.frequency,
          duration: it.duration,
          instructions: it.instructions,
          quantity_prescribed: 1,
          unit: 'buc',
        })),
        internal_notes: notes || null,
      });

      alert('Reteta salvata cu succes!');
      setDialogOpen(false);
      resetForm();
      loadPrescriptions();
    } catch (error) {
      console.error('Eroare la salvarea retetei:', error);
      alert('Eroare la salvarea retetei');
    }
  };

  const resetForm = () => {
    setSelectedPatientId('');
    setPrescriptionItems([{ medication_name: '', dosage: '', frequency: '', duration: '', instructions: '' }]);
    setNotes('');
  };

  const handleDownloadPDF = async (prescriptionId: string) => {
    try {
      const response = await api.get(`/prescriptions/${prescriptionId}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reteta-${prescriptionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Eroare la descarcarea PDF:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Retete</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Reteta Noua
        </Button>
      </Box>

      {/* List */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : prescriptions.length === 0 ? (
        <Alert severity="info">Nu exista retete emise</Alert>
      ) : (
        <Stack spacing={2}>
          {prescriptions.map((prescription) => (
            <Card key={prescription.id}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <MedicationIcon color="primary" />
                    <Box>
                      <Typography variant="h6">
                        {prescription.patient?.name || 'Pacient necunoscut'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(prescription.created_at), 'dd MMMM yyyy, HH:mm')}
                      </Typography>
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Chip label={prescription.status} color="primary" size="small" />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadPDF(prescription.id)}
                    >
                      PDF
                    </Button>
                  </Stack>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Medicamente prescrise:
                </Typography>
                <List dense>
                  {prescription.items.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={item.medication_name}
                        secondary={
                          <>
                            {item.dosage} • {item.frequency} • {item.duration}
                            {item.instructions && ` • ${item.instructions}`}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* New Prescription Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Reteta Noua</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Patient Selection - in productie ar fi un autocomplete */}
            <TextField
              fullWidth
              label="ID Pacient"
              value={selectedPatientId}
              onChange={(e) => setSelectedPatientId(e.target.value)}
              helperText="In productie: autocomplete cu cautare pacient"
            />

            <Divider />

            <Typography variant="h6">Medicamente</Typography>

            {prescriptionItems.map((item, index) => (
              <Card key={index} variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle2">Medicament #{index + 1}</Typography>
                    {prescriptionItems.length > 1 && (
                      <IconButton size="small" onClick={() => handleRemoveItem(index)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        label="Nume Medicament *"
                        value={item.medication_name}
                        onChange={(e) => handleItemChange(index, 'medication_name', e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Dozaj *"
                        value={item.dosage}
                        onChange={(e) => handleItemChange(index, 'dosage', e.target.value)}
                        placeholder="Ex: 10mg"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Frecventa"
                        value={item.frequency}
                        onChange={(e) => handleItemChange(index, 'frequency', e.target.value)}
                        placeholder="Ex: 2x/zi"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Durata"
                        value={item.duration}
                        onChange={(e) => handleItemChange(index, 'duration', e.target.value)}
                        placeholder="Ex: 7 zile"
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        label="Instructiuni"
                        value={item.instructions}
                        onChange={(e) => handleItemChange(index, 'instructions', e.target.value)}
                        placeholder="Ex: Se administreaza dupa masa"
                        multiline
                        rows={2}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
            >
              Adauga Medicament
            </Button>

            <TextField
              fullWidth
              label="Note Suplimentare"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={3}
              placeholder="Recomandari generale, precautii..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Anuleaza</Button>
          <Button variant="contained" onClick={handleSavePrescription}>
            Salveaza & Genereaza PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DoctorPrescriptions;

